import { useMemo, useState } from 'react';
import { addMonths } from 'date-fns';
import { parseDate, today } from '@link/share/utils';
import type { CalendarDay } from '../stockStatus';

/** How far the calendar has been loaded, and loading more of it. */
export interface CalendarPaging {
  /** Last day loaded; null before the first page. */
  loadedUntil: Date | null;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

interface Options {
  /** The selected date (YYYY-MM-DD); '' for none. */
  value: string;
  days: CalendarDay[];
  paging: CalendarPaging;
}

const monthIndex = (date: Date) => date.getFullYear() * 12 + date.getMonth();

/** YYYY-MM in local time, comparable with the first 7 characters of a day. */
export const monthKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const firstOfMonth = (date: string) => {
  const parsed = parseDate(date);
  return new Date(parsed.getFullYear(), parsed.getMonth(), 1);
};

/**
 * The month on screen and the rules for leaving it.
 *
 * Opens on the selected date's month (else this month) and follows the
 * selection when it is moved from outside, e.g. a plan switch landing on
 * another date.
 *
 * Moving forward past the loaded range asks for the next page. Inside it, the
 * API only returns days that have stock, so a month can be empty while later
 * ones are not; "next" stays enabled as long as any later day is loaded.
 */
export const useVisibleMonth = ({ value, days, paging }: Options) => {
  const { loadedUntil, hasMore, isLoadingMore, onLoadMore } = paging;
  const [month, setMonth] = useState(() => firstOfMonth(value || today()));

  // Follow a selection moved from outside. Adjusted during render rather than
  // in an effect, so the old month never paints.
  const [followedValue, setFollowedValue] = useState(value);
  if (value !== followedValue) {
    setFollowedValue(value);
    if (value) setMonth(firstOfMonth(value));
  }

  const lastDay = useMemo(
    () =>
      days.reduce(
        (last, day) => (day.date && day.date > last ? day.date : last),
        ''
      ),
    [days]
  );

  const current = monthIndex(month);
  const nextIsLoaded = !!loadedUntil && current + 1 <= monthIndex(loadedUntil);
  const hasLaterDays = lastDay.slice(0, 7) > monthKey(month);

  const canGoBack = current > monthIndex(parseDate(today()));
  const canGoForward =
    !isLoadingMore && (nextIsLoaded ? hasLaterDays : hasMore);
  /** The month on screen is the one being fetched. */
  const isMonthLoading =
    isLoadingMore && !!loadedUntil && current > monthIndex(loadedUntil);

  const goBack = () => setMonth(prev => addMonths(prev, -1));
  const goForward = () => {
    setMonth(prev => addMonths(prev, 1));
    if (!nextIsLoaded && hasMore && !isLoadingMore) onLoadMore();
  };

  return {
    month,
    canGoBack,
    canGoForward,
    isMonthLoading,
    goBack,
    goForward
  };
};
